import { Component, OnDestroy } from '@angular/core';
import { ChatService } from 'src/app/demo/service/chat.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, switchMap, distinctUntilChanged, map } from 'rxjs/operators';
import { UserNotificationsDTO } from 'src/app/demo/model/UserNotificationsDTO';
import { Timestamp } from 'firebase/firestore';
import { UserNotificationService } from 'src/app/demo/service/userNotification.service';
import { Router, UrlTree } from '@angular/router';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { Common } from '../../../utils/common';

@Component({
  selector: 'app-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss']
})
export class UserNotificationsComponent implements OnDestroy {
  notifications: UserNotificationsDTO[] = [];
  allNotifications: UserNotificationsDTO[] = [];
  notificationsCount: number = 0;
  userId: string = '';
  onlyShowUnread: boolean = true; 
  private notifSub!: Subscription;
  private timeRefreshInterval: any;
  selectedCategory: string | null = null;
  userData! : any;

  categories = [
    { label: 'Classrooms', value: 'Classrooms' },
    { label: 'Attendance', value: 'Attendance' },
    { label: 'Events & To Dos', value: 'Events & To Dos' },
    { label: 'Student', value: 'Student' },
    { label: 'Staff', value: 'Staff' },
    { label: 'Scheduling', value: 'Scheduling' },
    { label: 'Activities', value: 'Activities' },
    { label: 'Availability', value: 'Availability' },
    { label: 'Communication', value: 'Communication' },
    { label: 'Billing', value: 'Billing' },
    { label: 'Settings', value: 'Settings' }
  ];

  constructor(
    private chatService: ChatService,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private userNotificationService : UserNotificationService,
    private router : Router,
    private firestore: AngularFirestore
  ) {}

  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem('user-profile') || '{}');
    this.userData = user;
    
    if (user?.id) {
      this.userId = `user_${user.id}`;
      this.fetchNotifications();
      this.timeRefreshInterval = setInterval(() => this.refreshTimeDisplays(), 60000);
    }
  }

  fetchNotifications(): void {
    // Calculate date 7 days ago in school timezone (end of day)
    const todayInSchoolTZ = new Date();
    const sevenDaysAgoInSchoolTZ = new Date(todayInSchoolTZ);
    sevenDaysAgoInSchoolTZ.setDate(todayInSchoolTZ.getDate() - 7);

    // Convert to UTC using Common timezone utilities
    const sevenDaysAgoUTC = Common.getUtcStartOfDay(sevenDaysAgoInSchoolTZ);

    this.notifSub = this.firestore
      .collection(`userNotifications/${this.userId}/notifications`, (ref: any) =>
        ref.where('createdAt', '>=', firebase.firestore.Timestamp.fromDate(new Date(sevenDaysAgoUTC)))
           .orderBy('createdAt', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((notifs: any[]) => notifs.map(n => ({ ...n, id: n.id }))),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        catchError(err => {
          console.error('Error while fetching notifications:', err);
          return of([]);
        })
      )
      .subscribe((notifs: UserNotificationsDTO[]) => {
        this.allNotifications = notifs
          .filter(n => {
            const title = n?.title?.trim();
            return title && !title.endsWith('Messaging');
          })
          .map(n => ({ ...n, displayTimeAgo: this.getTimeAgo(n.createdAt) }));
        this.applyFilters();
      });
  }
  

  ngOnDestroy(): void {
    if (this.notifSub) {
      this.notifSub.unsubscribe();
    }
    clearInterval(this.timeRefreshInterval);
  }

  refreshTimeDisplays(): void {
    this.allNotifications = this.allNotifications.map(n => ({
      ...n,
      displayTimeAgo: this.getTimeAgo(n.createdAt)
    }));
    this.applyFilters();
  }

  getTimeAgo(createdAt: any): string {
    if (!createdAt) return 'Just now';
  
    // Handle Firestore Timestamp object
    let date: Date;
  
    if (createdAt instanceof Timestamp) {
      date = createdAt.toDate(); 
    } else if (typeof createdAt === 'string') {
      date = new Date(createdAt); 
    } else if (createdAt.seconds) {
      date = new Date(createdAt.seconds * 1000);
    } else {
      return 'Just now'; 
    }
  
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  
    if (diff < 60) return `${diff} sec ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
  
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getTagClass(title: string): string {
    
    if (!title) return 'bg-gray-100 text-gray-700';
  
    const colors = [
      { bg: 'bg-green-100', text: 'text-green-700' },
      { bg: 'bg-blue-100', text: 'text-blue-700' },
      { bg: 'bg-red-100', text: 'text-red-700' },
      { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      { bg: 'bg-purple-100', text: 'text-purple-700' },
      { bg: 'bg-pink-100', text: 'text-pink-700' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      { bg: 'bg-teal-100', text: 'text-teal-700' },
      { bg: 'bg-orange-100', text: 'text-orange-700' },
      { bg: 'bg-cyan-100', text: 'text-cyan-700' },
      { bg: 'bg-teal-100', text: 'text-teal-700' }
    ];
  
    const hash = [...title.toLowerCase()].reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % colors.length;
    const color = colors[colorIndex];
  
    return `${color.bg} ${color.text}`;
  }
  

  getInitials(name: string): string {
    if (!name) return '';
    return name.split(' ').map(word => word[0]?.toUpperCase()).join('').slice(0, 2);
  }

  onCategoryChange(event: any): void {
    this.selectedCategory = event?.value;
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.allNotifications];

    if (this.onlyShowUnread) {
      filtered = filtered.filter(n => !n?.read);
    }
  
    if (this.selectedCategory) {
      filtered = filtered.filter(n => n?.title === this.selectedCategory);
    }
  
    this.notifications = filtered;
  }


  markAsRead(notif: UserNotificationsDTO, event: Event): void {
    event.stopPropagation(); // Prevent triggering the whole card click
    if (!notif.read) {
      this.userNotificationService
        .markNotificationAsRead(this.userId, notif.id)
        .then(() => {
          notif.read = true;
        })
        .catch((err) => {
          console.error('Failed to mark notification as read:', err);
        });
    }
  }

  markAllAsRead(): void {
    this.userNotificationService.markAllNotificationsAsRead(this.userId)
      .then(() => {
        this.allNotifications.forEach(notif => notif.read = true);
        this.applyFilters();
      })
      .catch((error) => {
        console.error('Error marking all as read:', error);
      });
  }
  
  
  
  private readonly screenRoutes: Record<string, string> = {
    'activity': '/parentConnection/activities/all',
    'attendance': '/student-Sign-In/dailyAttendance',
    'classroom': '/myschool/room',
    'student': '/myschool/student',
    'staff': '/staff/overview',
    'scheduling': '/scheduling/classroom-view',
    'communication': '/parentConnection/newsletter/sentLetter',
    'billing': '/billing/accounting/family',
    'settings': '/settings/school',
  };

  redirectToPage(notif: UserNotificationsDTO): void {
    const updateReadPromise = !notif.read
      ? this.userNotificationService.markNotificationAsRead(this.userId, notif.id).then(() => {
          notif.read = true;
        })
      : Promise.resolve();

    let route: string | UrlTree = this.getRouteForCategory(notif.title);

    if (!route && notif['screen']) {
      route = this.screenRoutes[notif['screen'].toLowerCase()] ?? '';
    }

    updateReadPromise.then(() => {
      if (route) {
        this.ref.close();
        setTimeout(() => {
          this.router.navigateByUrl(route as any);
        }, 100);
      }
    });
  }
  


  getRouteForCategory(category: string): string | UrlTree { //added UrlTree as a return type
    const hasEnrollmentAccess = this.userData?.authorities?.includes('Enrollment (Full Access)');
    const refTable = (this.userData?.refTable || '').toString().trim().toLowerCase();
    const isParent = refTable === 'parent' || refTable === 'guardian';
    
    switch (category) {
      case 'Classrooms': return '/myschool/room';
      case 'Student': return '/myschool/student';
      case 'Attendance': return '/student-Sign-In/dailyAttendance';
      case 'Events & To Dos': return '/myschool/events';
      case 'Staff': return '/staff/overview';
      case 'Scheduling': return '/scheduling/classroom-view';
      case 'Activity': return '/parentConnection/activities/all';
      case 'Availability':
        return hasEnrollmentAccess ? '/future-enrollment/future-availability' : ''; 
      case 'Communication': return '/parentConnection/newsletter/sentLetter';
      case 'Billing': 
        if (isParent) {
          const activeStudentRaw = localStorage.getItem('activeStudent');
          const activeStudent = activeStudentRaw ? JSON.parse(activeStudentRaw) : null;
          
          if (activeStudent?.id) {
            //return `/billings/accounting/family-transaction/${activeStudent.id}`;
            
            return this.router.createUrlTree(
              ['/billing/accounting/familylayout/family-transaction'],
              { queryParams: { id: activeStudent.id }}
            );   
          } 
          else {
            // If no active student, return empty string to prevent navigation
            // console.warn('No active student found for parent billing navigation');
            return '';
          }
        } else {
          return '/billing/accounting/family';
        }
      case 'Settings': return '/settings/school';
      default: return '';
    }
  }
  
  
  
  
}
